import React, { useEffect, useState, useCallback } from "react";
import { db } from "../../services/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import "../../styles/ProfitReport.css";

const ProfitReport = () => {
    const [compiledOrders, setCompiledOrders] = useState([]);
    const [stockPrices, setStockPrices] = useState({});
    const [monthlyProfit, setMonthlyProfit] = useState({});
    const [totalProfit, setTotalProfit] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            // 1️⃣ compiled orders
            const ordersSnapshot = await getDocs(
                collection(db, "inventori", "selangor", "compiledOrders")
            );
            const orders = ordersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            setCompiledOrders(orders);

            // 2️⃣ stock prices
            const pricesSnapshot = await getDocs(
                collection(db, "inventori", "selangor", "stocks")
            );
            const prices = {};
            pricesSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const itemName = data.name;
                if (itemName) prices[itemName] = data.harga || {};
            });
            setStockPrices(prices);
        };

        fetchData();
    }, []);

    const toNumberSafe = (v) => {
        if (v == null) return 0;

        if (typeof v === "object") {
            const n = Number(v.total ?? v.ordered ?? v.qty ?? 0);
            return Number.isFinite(n) ? n : 0;
        }

        if (typeof v === "string") {
            const m = v.match(/-?\d+(\.\d+)?/);
            const n = m ? Number(m[0]) : 0;
            return Number.isFinite(n) ? n : 0;
        }

        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const calcOrderProfit = useCallback(
        (order) => {
            let orderProfit = 0;

            Object.entries(order.items || {}).forEach(([category, products]) => {
                Object.entries(products || {}).forEach(([itemName, detail]) => {
                    const hargaObj = stockPrices[itemName];
                    if (!hargaObj) return;

                    // ✅ BAJU (ikut size)
                    if (category === "baju" && detail && typeof detail === "object") {
                        Object.entries(detail).forEach(([size, qtyRaw]) => {
                            const qty = toNumberSafe(qtyRaw);
                            const pengurus = toNumberSafe(hargaObj?.[size]?.pengurus);
                            const pengerusi = toNumberSafe(hargaObj?.[size]?.pengerusi);
                            orderProfit += qty * (pengurus - pengerusi);
                        });
                        return;
                    }

                    // ✅ Buku pecahan tahun / object lain
                    if (detail && typeof detail === "object") {
                        Object.entries(detail).forEach(([key, qtyRaw]) => {
                            const qty = toNumberSafe(qtyRaw);
                            const perKey = hargaObj?.[key];

                            const pengurus = toNumberSafe(perKey?.pengurus ?? hargaObj?.pengurus);
                            const pengerusi = toNumberSafe(perKey?.pengerusi ?? hargaObj?.pengerusi);

                            orderProfit += qty * (pengurus - pengerusi);
                        });
                        return;
                    }

                    // ✅ Flat qty
                    const qty = toNumberSafe(detail);
                    const pengurus = toNumberSafe(hargaObj?.pengurus);
                    const pengerusi = toNumberSafe(hargaObj?.pengerusi);
                    orderProfit += qty * (pengurus - pengerusi);
                });
            });

            return orderProfit;
        },
        [stockPrices]
    );

    useEffect(() => {
        if (compiledOrders.length === 0 || Object.keys(stockPrices).length === 0) return;

        let overall = 0;
        const monthly = {};

        compiledOrders.forEach((order) => {
            const orderDate = order.orderDate?.toDate?.() || new Date(order.orderDate);
            const monthKey = `${orderDate.getMonth() + 1}/${orderDate.getFullYear()}`;

            const profit = calcOrderProfit(order);

            overall += profit;
            monthly[monthKey] = (monthly[monthKey] || 0) + profit;
        });

        setTotalProfit(overall);
        setMonthlyProfit(monthly);
    }, [compiledOrders, stockPrices, calcOrderProfit]);

    const formatRM = (n) =>
        new Intl.NumberFormat("en-MY", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(n || 0));


    return (
        <div className="profit-report-container">
            <h2>Laporan Untung Keseluruhan</h2>

            <div className="summary-box">
                <strong>Jumlah Untung Keseluruhan : </strong>
                <span className="highlight">RM {formatRM(totalProfit)}</span>
            </div>

            <h3>Untung Mengikut Bulan</h3>
            <table className="profit-table">
                <thead>
                    <tr>
                        <th>Bulan</th>
                        <th>Jumlah Untung (RM)</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(monthlyProfit).map(([month, profit]) => (
                        <tr key={month}>
                            <td>{month}</td>
                            <td>RM {formatRM(profit)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <br />
            <h3>Senarai Semua Tempahan</h3>
            <table className="profit-table">
                <thead>
                    <tr>
                        <th>Tarikh</th>
                        <th>Kawasan</th>
                        <th>Pengerusi</th>
                        <th>Untung Tempahan (RM)</th>
                    </tr>
                </thead>
                <tbody>
                    {compiledOrders.map((order) => {
                        const date =
                            order.orderDate?.toDate?.().toLocaleDateString("ms-MY") || "-";
                        const district = order.district || "-";
                        const pengerusi = order.pengerusiName || "-";
                        const profit = calcOrderProfit(order);

                        return (
                            <tr key={order.id}>
                                <td>{date}</td>
                                <td>{district}</td>
                                <td>{pengerusi}</td>
                                <td>RM {formatRM(profit)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ProfitReport;
